import React from "react";
import Link from "next/link";
import Image from "components/Image/CustomImage";

import useTranslation from "next-translate/useTranslation";

import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";

import { ATHLETES_ID, SHOW_STRAVA_LOGIN } from "settings/global-website-config";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import SkeletonWithFade from "components/Loading/SkeletonWithFade";

import { makeStyles } from "tss-react/mui";

import { getAuthorisedImage } from "modules/utils";

const useStyles = makeStyles()((theme) => ({
  fallbackAvatar: {
    "&:hover": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  center: {
    justifyContent: "center",
    marginTop: "4px",
  },
  left: {
    justifyContent: "start",
    marginTop: "4px",
  },
}));

const AvatarInnerForwardRef = (props, ref) => {
  const { classes } = useStyles();

  const { src, alt, unoptimized, ...rest } = props;
  return (
    <Avatar
      classes={{ root: classes.fallbackAvatar }}
      component="a"
      {...rest}
      ref={ref}
    >
      {src && (
        <Image
          src={src}
          fill
          alt={alt}
          unoptimized={unoptimized}
          sizes="40px"
        />
      )}
    </Avatar>
  );
};

const AvatarInner = React.forwardRef(AvatarInnerForwardRef);

const linkableAvatarforwardRef = (props, ref) => {
  const { href, ...rest } = props;
  //  Spread the props to the underlying DOM element.
  return href ? (
    <Link prefetch={false} href={href} passHref legacyBehavior>
      <AvatarInner {...rest} ref={ref} />
    </Link>
  ) : (
    <AvatarInner {...rest} ref={ref} />
  );
};
if (process.env.NODE_ENV !== "production") {
  linkableAvatarforwardRef.displayName = "linkableAvatarforwardRef";
}

export const LinkableAvatar = React.forwardRef(linkableAvatarforwardRef);

const tooltipAvatarForwardRef = (props, ref) => {
  const { title, ...rest } = props;
  //  Spread the props to the underlying DOM element.
  return (
    <Tooltip title={title} fontSize="small">
      <LinkableAvatar {...rest} alt={title} ref={ref} />
    </Tooltip>
  );
};
if (process.env.NODE_ENV !== "production") {
  tooltipAvatarForwardRef.displayName = "tooltipAvatarForwardRef";
}

const TooltipAvatar = React.forwardRef(tooltipAvatarForwardRef);

export default function ProductAthletesAvatarGroup(props) {
  const {
    href,
    productAthletes,
    center,
    left,
    showTitle,
    max,
    loading,
    athleteType,
  } = props;
  const { classes, cx } = useStyles();

  const hrefToUse = href ? `${href}#${ATHLETES_ID}` : null;

  const { t } = useTranslation();

  // Could use custom version incorporating tooltips and proper linking: https://stackoverflow.com/questions/62782306/how-can-i-add-a-tooltip-to-the-avatargroup-x-bubble

  return productAthletes?.edges?.length ? (
    <div>
      {showTitle && (
        <SkeletonWithFade inProp={loading}>
          <h6>
            {athleteType === "pro"
              ? t("product:proAthleteUsers")
              : t("product:followingWithProduct")}
          </h6>
        </SkeletonWithFade>
      )}
      <SkeletonWithFade inProp={loading}>
        <AvatarGroup
          max={max}
          spacing="medium"
          classes={{
            root: cx({ [classes.center]: center, [classes.left]: left }),
          }}
        >
          {productAthletes.edges?.map((athlete) => (
            <TooltipAvatar
              key={`${athlete.node.id}`}
              title={athlete.node.name}
              href={SHOW_STRAVA_LOGIN ? athlete.node.uri : hrefToUse}
              alt={athlete.node.name}
              src={getAuthorisedImage(athlete)}
              unoptimized={athlete.node.integration.accountAuthorised}
            />
          ))}
        </AvatarGroup>
      </SkeletonWithFade>
    </div>
  ) : null;
}

ProductAthletesAvatarGroup.defaultProps = { athleteType: "pro" };

ProductAthletesAvatarGroup.propTypes = {
  // featuredImage: PropTypes.object,
  // fallbackAltText: PropTypes.string.isRequired,
  href: PropTypes.string,
  productAthletes: PropTypes.object,
  type: PropTypes.string,
};
